import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { markInitialAuthCheckComplete } from '../services/api';

declare global {
  interface Window {
    authContext?: any;
  }
}

interface AuthContextType {
  isAuthenticated: boolean;
  userId: number | null;
  login: (email: string, password: string, redirectPath?: string) => Promise<void>;
  register: (email: string, password: string, firstName: string, lastName: string, redirectPath?: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, newPassword: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  logout: () => Promise<void>;
  authenticateWithGoogle: (code?: string) => Promise<void>;
  authenticateWithToken: (token: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userId, setUserId] = useState<number | null>(null);
  const [hasFetchedUser, setHasFetchedUser] = useState(false);
  const [isFetchingUser, setIsFetchingUser] = useState(false);
  const navigate = useNavigate();
  
  // Use ref to track if we've attempted auth check - prevents loops
  const hasAttemptedAuthCheck = React.useRef(false);

  useEffect(() => {
    // Only run once on mount - use ref to prevent re-runs
    if (hasAttemptedAuthCheck.current) {
      return;
    }
    
    // Check authentication by attempting to fetch user info
    // Cookie is sent automatically if it exists
    if (!hasFetchedUser && !isFetchingUser) {
      hasAttemptedAuthCheck.current = true;
      setIsFetchingUser(true);
      
      // Wrap in async function to handle errors silently
      // Use void to explicitly ignore the promise and prevent unhandled rejections
      void (async () => {
        try {
          const response = await authService.getCurrentUser();
          
          // Check if this is an expected 401 response (from interceptor)
          if ((response as any)?.isExpected401) {
            // This is an expected 401 during initial auth check
            setIsAuthenticated(false);
            setUserId(null);
            setHasFetchedUser(true);
            setIsFetchingUser(false);
            markInitialAuthCheckComplete();
            return; // Silently handle - no error thrown
          }
          
          // Normal successful response
          setIsAuthenticated(true);
          setUserId((response as any).id);
          setHasFetchedUser(true);
          setIsFetchingUser(false);
          markInitialAuthCheckComplete();
        } catch (error: any) {
          // Always mark as fetched to prevent infinite loops
          setHasFetchedUser(true);
          setIsFetchingUser(false);
          markInitialAuthCheckComplete();
          
          // Silently handle 401 during initial check - this is expected when not logged in
          if (error.response?.status === 401 || error.isExpected) {
            setIsAuthenticated(false);
            setUserId(null);
            // Completely suppress this error - don't log, don't throw, don't propagate
            return;
          }
          
          // Network errors (CORS, connection issues, etc.) - treat as not authenticated
          // This prevents infinite loops when backend is unreachable
          if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
            // Only log in development, not in production
            if (process.env.NODE_ENV === 'development') {
              console.warn('Network error during auth check - treating as not authenticated');
            }
            setIsAuthenticated(false);
            setUserId(null);
          } else {
            // Other errors - only log in development
            if (process.env.NODE_ENV === 'development') {
              console.error('Failed to fetch user info:', error);
            }
            setIsAuthenticated(false);
            setUserId(null);
          }
        }
      })();
    }
  }, []); // Empty dependency array - only run once on mount

  const login = async (email: string, password: string, redirectPath: string = '/') => {
    try {
      const response = await authService.login(email, password);
      // Cookie is set by backend, now fetch user info
      setIsFetchingUser(true);
      
      let userInfo;
      if (response.user) {
        // Use user object from login response if available
        userInfo = response.user;
      } else {
        // Fallback: fetch user info
        // Add a small delay to ensure cookie is set
        await new Promise(resolve => setTimeout(resolve, 100));
        try {
          userInfo = await authService.getCurrentUser();
        } catch (profileError: any) {
          // If profile fetch fails with 401, cookie might not be set yet
          if (profileError.response?.status === 401) {
            console.warn('Profile fetch failed after login - cookie may not be set yet');
            // Try one more time after a longer delay
            await new Promise(resolve => setTimeout(resolve, 500));
            userInfo = await authService.getCurrentUser();
          } else {
            throw profileError;
          }
        }
      }
      
      setIsAuthenticated(true);
      setUserId(userInfo.id);
      setHasFetchedUser(true);
      setIsFetchingUser(false);
      markInitialAuthCheckComplete(); // Mark as complete after successful login
      
      navigate(redirectPath);
    } catch (error) {
      console.error('Login error:', error);
      setIsAuthenticated(false);
      setUserId(null);
      setIsFetchingUser(false);
      throw error;
    }
  };

  const register = async (email: string, password: string, firstName: string, lastName: string, redirectPath: string = '/') => {
    try {
      const response = await authService.register(email, password, firstName, lastName);
      // Cookie is set by backend, now fetch user info
      setIsFetchingUser(true);
      
      let userInfo;
      if (response.user) {
        // Use user object from register response if available
        userInfo = response.user;
      } else {
        // Fallback: fetch user info
        // Add a small delay to ensure cookie is set
        await new Promise(resolve => setTimeout(resolve, 100));
        try {
          userInfo = await authService.getCurrentUser();
        } catch (profileError: any) {
          // If profile fetch fails with 401, cookie might not be set yet
          if (profileError.response?.status === 401) {
            console.warn('Profile fetch failed after registration - cookie may not be set yet');
            // Try one more time after a longer delay
            await new Promise(resolve => setTimeout(resolve, 500));
            userInfo = await authService.getCurrentUser();
          } else {
            throw profileError;
          }
        }
      }
      
      setIsAuthenticated(true);
      setUserId(userInfo.id);
      setHasFetchedUser(true);
      setIsFetchingUser(false);
      markInitialAuthCheckComplete(); // Mark as complete after successful registration
      
      navigate(redirectPath);
    } catch (error) {
      console.error('Registration error:', error);
      setIsAuthenticated(false);
      setUserId(null);
      setIsFetchingUser(false);
      throw error;
    }
  };

  const forgotPassword = async (email: string) => {
    try {
      await authService.forgotPassword(email);
    } catch (error) {
      console.error('Forgot password error:', error);
      throw error;
    }
  };

  const resetPassword = async (token: string, newPassword: string) => {
    try {
      await authService.resetPassword(token, newPassword);
    } catch (error) {
      console.error('Reset password error:', error);
      throw error;
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    try {
      await authService.changePassword(currentPassword, newPassword);
    } catch (error) {
      console.error('Change password error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear all auth state regardless of logout API call result
      setIsAuthenticated(false);
      setUserId(null);
      setHasFetchedUser(false);
      hasAttemptedAuthCheck.current = false; // Reset so auth check can run again after login
      navigate('/login');
    }
  };

  const authenticateWithGoogle = async (code?: string) => {
    try {
      setIsFetchingUser(true);
      
      let userInfo;
      
      if (code) {
        // If code is present, call backend to exchange it for tokens and set cookie
        const response = await authService.handleGoogleCallback(code);
        if (response.user) {
          // Use user object from OAuth response if available
          userInfo = response.user;
        } else {
          // Fallback: fetch user info (cookie should be set by backend now)
          // Add a small delay to ensure cookie is set
          await new Promise(resolve => setTimeout(resolve, 200));
          userInfo = await authService.getCurrentUser();
          
          // Verify cookie was actually set
          if ((userInfo as any)?.isExpected401) {
            throw new Error('Authentication failed - cookie was not set by backend');
          }
        }
      } else {
        // No code - backend likely already processed the callback server-side
        // Just verify authentication by fetching user info
        userInfo = await authService.getCurrentUser();
        
        // Check if this is an expected 401 (not authenticated)
        if ((userInfo as any)?.isExpected401) {
          throw new Error('Authentication failed - no valid session found');
        }
      }
      
      // Verify we got valid user info
      if (!userInfo || !userInfo.id) {
        throw new Error('Authentication failed - invalid user data received');
      }
      
      setIsAuthenticated(true);
      setUserId(userInfo.id);
      setHasFetchedUser(true);
      setIsFetchingUser(false);
    } catch (error) {
      console.error('Google authentication error:', error);
      setIsAuthenticated(false);
      setUserId(null);
      setIsFetchingUser(false);
      throw error;
    }
  };

  const authenticateWithToken = async (token: string) => {
    // Note: With httpOnly cookies, token-based auth should be handled by backend
    // If backend supports token-to-cookie conversion, that should happen server-side
    // For now, try to fetch user info - if cookie was set by backend, it will work
    try {
      setIsFetchingUser(true);
      const userInfo = await authService.getCurrentUser();
      setIsAuthenticated(true);
      setUserId(userInfo.id);
      setHasFetchedUser(true);
      setIsFetchingUser(false);
    } catch (error) {
      console.error('Token authentication error:', error);
      setIsAuthenticated(false);
      setUserId(null);
      setHasFetchedUser(false);
      setIsFetchingUser(false);
      throw error;
    }
  };

  const contextValue = { isAuthenticated, userId, login, register, forgotPassword, resetPassword, changePassword, logout, authenticateWithGoogle, authenticateWithToken };

  // Expose to window for debugging
  if (typeof window !== "undefined") {
    window.authContext = contextValue;
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
